import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Switch, SvgIcon, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import { Stack } from '@mui/system';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useDispatch, useSelector } from 'react-redux';
import { paths } from 'src/paths';
import { getAccessToken } from 'src/routes/AuthContext';
import {
  getSubmissionDocumentAction,
  resetGetSubmissionDocumentAction,
} from 'src/redux/actions/documentationAction';

type Props = {};

function PreviewDocument(props: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { patients, patientDetails, episodeOfCare, details, physicianSign } = paths;
  const getPatientId = useSelector((state: any) => state?.getPatientIdReducer);

  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>(location?.state?.document?.pdfUrl ?? '');
  const submissionDocumentResponse = useSelector(
    (state: any) => state?.getSubmissionDocumentReducer
  );

  useEffect(() => {
    if (submissionDocumentResponse?.isSuccess && submissionDocumentResponse?.response?.data) {
      setPdfUrl(submissionDocumentResponse?.response?.data);
    }
  }, [submissionDocumentResponse]);

  useEffect(() => {
    if (!location?.state?.signedDocuments) return;
    dispatch(
      getSubmissionDocumentAction({
        submissionId: location?.state?.document?.uuid,
        showAuditTrail: false,
      })
    );
  }, [location?.state?.signedDocuments]);

  useEffect(() => {
    return () => {
      dispatch(resetGetSubmissionDocumentAction());
    };
  }, [dispatch]);

  const handleToggleAuditTrail = (_e: any, checked: boolean) => {
    setShowAuditTrail(checked);
    dispatch(
      getSubmissionDocumentAction({
        submissionId: location?.state?.document?.uuid,
        showAuditTrail: checked,
      })
    );
  };

  const [iframeRef, setIframeRef] = useState<any | null>(null);

  const updateIFrameRef = useCallback((node: any) => {
    setIframeRef(node);
  }, []);

  useEffect(() => {
    const iframeElement = iframeRef;

    if (iframeElement) {
      const interval = setInterval(async () => {
        const response = await getAccessToken();
      }, 15000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [iframeRef]);

  const goBackToEOC = () => {
    if (location.state?.source === 'physician-sign') {
      navigate(`${patients}/${patientDetails}${episodeOfCare}${details}${physicianSign}`, {
        state: {
          episodeOfCareId: location?.state?.episodeOfCareId,
          getPatientId,
        },
      });
      return;
    }
    navigate(`${patients}/${patientDetails}${episodeOfCare}${details}`, {
      state: {
        episodeOfCareId: location?.state?.episodeOfCareId,
        getPatientId,
      },
    });
  };

  return (
    <>
      <Stack
        gap="1.5rem"
        sx={{ padding: '0 4rem' }}
      >
        <Button
          sx={{
            justifyContent: 'flex-start',
            color: '#111927',
            width: 'max-content',
            padding: '0px',
          }}
          startIcon={
            <SvgIcon>
              <ArrowBackIcon sx={{ color: '#6C737F' }} />
            </SvgIcon>
          }
          onClick={goBackToEOC}
        >
          <Typography variant="subtitle1">
            Back to Episode Of Care Details -
            {` ${location?.state?.patientData?.lastName ?? ''} ${
              location?.state?.patientData?.firstName ?? ''
            }`}
          </Typography>
        </Button>
        <Stack
          flexDirection="row"
          gap="1rem"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h5">
            Preview Document - {location?.state?.document?.documentType}
          </Typography>
          {location?.state?.signedDocuments && (
            <Stack
              flexDirection="row"
              alignItems="center"
              gap="0.5rem"
            >
              <Switch
                checked={showAuditTrail}
                onChange={handleToggleAuditTrail}
              />
              <Typography variant="body2">Show audit trail</Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
      <Box
        sx={{ height: '100%' }}
        p={3}
      >
        {submissionDocumentResponse?.loading ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <iframe
            src={pdfUrl}
            ref={updateIFrameRef}
            width={'100%'}
            height={'100%'}
            style={{ border: 'none' }}
          />
        )}
      </Box>
    </>
  );
}

export default PreviewDocument;
